'use client';

import { createContext, useContext, useEffect, useMemo } from 'react';
import { usePathname } from 'next/navigation';

import {
  DEFAULT_WORKSPACE_SECTION,
  deriveSurfaceFromPath,
  deriveWorkspaceSectionFromPath,
  setStoredSurface,
  setStoredWorkspaceSection,
  type Surface,
} from '@/lib/session/default-surface';

interface SurfaceContextValue {
  surface: Surface;
}

const SurfaceContext = createContext<SurfaceContextValue | null>(null);

const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days

function writeCookie(name: string, value: string) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=${encodeURIComponent(
    value,
  )}; path=/; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
}

export function SurfaceProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() ?? '/';
  const isArtifactPreview = pathname.startsWith('/artifact-preview/');
  const surface = useMemo(() => deriveSurfaceFromPath(pathname), [pathname]);
  const workspaceSection = useMemo(
    () => deriveWorkspaceSectionFromPath(pathname),
    [pathname],
  );

  useEffect(() => {
    if (isArtifactPreview) return;
    setStoredSurface(surface);
    writeCookie('mh_last_surface', surface);
  }, [isArtifactPreview, surface]);

  useEffect(() => {
    if (isArtifactPreview) return;
    if (surface === 'workspace') {
      const section = workspaceSection ?? DEFAULT_WORKSPACE_SECTION;
      setStoredWorkspaceSection(section);
      writeCookie('mh_last_workspace_section', section);
    }
  }, [isArtifactPreview, surface, workspaceSection]);

  const value = useMemo<SurfaceContextValue>(() => ({ surface }), [surface]);

  return (
    <SurfaceContext.Provider value={value}>{children}</SurfaceContext.Provider>
  );
}

export function useSurface(): Surface {
  const ctx = useContext(SurfaceContext);
  if (!ctx) {
    throw new Error('useSurface must be used inside <SurfaceProvider>');
  }
  return ctx.surface;
}

export function useOptionalSurface(): Surface | null {
  const ctx = useContext(SurfaceContext);
  return ctx?.surface ?? null;
}
